import {
  type ComponentProps,
  createContext,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { cn } from '../../common/functions/cn';
import { matchesShortcut } from '../rich-text/shortcuts';

const SIDEBAR_STORAGE_KEY = 'odin:sidebar';
const SIDEBAR_MIN_WIDTH = 192;
const SIDEBAR_MAX_WIDTH = 384;
/** Where a drag shuts the panel rather than holding it at its floor, in the room under that floor. */
const SIDEBAR_COLLAPSE_WIDTH = 120;
const SIDEBAR_DRAWER_QUERY = '(width < 48rem)';

const readStoredState = (storageKey: string) => {
  try {
    // A missing or junk entry is no choice at all, which leaves the caller its default.
    const stored = localStorage.getItem(`${storageKey}-open`);
    return stored === 'true' || stored === 'false' ? stored === 'true' : undefined;
  } catch {
    return undefined;
  }
};

const writeStoredState = (storageKey: string, open: boolean) => {
  try {
    localStorage.setItem(`${storageKey}-open`, String(open));
  } catch {
    // Blocked or over quota
  }
};

const getIsDrawer = () => window.matchMedia(SIDEBAR_DRAWER_QUERY).matches;

const subscribeToDrawer = (listener: () => void) => {
  const query = window.matchMedia(SIDEBAR_DRAWER_QUERY);
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
};

const clampWidth = (width: number) => Math.min(Math.max(width, SIDEBAR_MIN_WIDTH), SIDEBAR_MAX_WIDTH);

const readStoredWidth = (storageKey: string) => {
  try {
    // A missing, empty or junk entry all read as falsy, which is the stylesheet's default.
    const stored = Number(localStorage.getItem(`${storageKey}-width`));
    return stored ? clampWidth(stored) : undefined;
  } catch {
    return undefined;
  }
};

const writeStoredWidth = (storageKey: string, width: number) => {
  try {
    localStorage.setItem(`${storageKey}-width`, String(width));
  } catch {
    // Blocked or over quota
  }
};

interface SidebarContextProps {
  state: 'expanded' | 'collapsed';
  /** Whether the rail is expanded. A drawer is always its full width, so it always reads open. */
  open: boolean;
  /** Which edge of the page the panel holds. */
  side: 'left' | 'right';
  /** Whether the panel floats over the page instead of standing beside it. */
  isDrawer: boolean;
  /** The shortcut that toggles the panel, for the trigger to name. Undefined when it has none. */
  keyboardShortcut: string | undefined;
  toggleSidebar: () => void;
  sidebarRef: RefObject<HTMLElement | null>;
  /** The panel's id, so a trigger outside it can be the popover's invoker. */
  sidebarId: string;
  /** A width restored from storage, which the panel carries inline. */
  initialWidth: number | undefined;
  /**
   * Drags the rail, resizing the panel until the pointer is released, and opening or shutting it
   * as the drag crosses the width it collapses at.
   */
  startResize: (event: ReactPointerEvent) => void;
  /** Widens or narrows the expanded panel by a step, for the rail's arrow keys. */
  resizeBy: (delta: number) => void;
}

const SidebarContext = createContext<SidebarContextProps | null>(null);

const useSidebar = () => {
  const context = use(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
};

interface SidebarProviderProps {
  /**
   * The rail state to start in before it has ever been toggled. A stored choice wins over it.
   * A drawer starts shut whatever this says, since the popover owns its openness.
   */
  defaultOpen?: boolean;
  /**
   * Which edge of the page the panel holds. A right-hand panel is written after the page it sits
   * beside, and takes the edge whichever way round it is written.
   */
  side?: 'left' | 'right';
  /**
   * `auto` stands the panel beside the page and turns it into a drawer below the breakpoint.
   * `drawer` floats it over the page at any width, for a page that leaves it nothing to stand beside.
   * `rail` never floats, for a panel whose only trigger is its own rail.
   */
  mode?: 'auto' | 'rail' | 'drawer';
  /**
   * The shortcut that toggles the panel, in the `Mod-B` format `formatShortcut` renders, where
   * `Mod` is ⌘ on a Mac and Ctrl elsewhere. Without one the panel has no shortcut, so two panels
   * on a page take different keys rather than fighting over the same one.
   */
  keyboardShortcut?: string;
  /**
   * Namespaces the persisted open state and width. Two panels on a page need different keys,
   * or they overwrite each other's. Only its first value is read.
   */
  storageKey?: string;
  /**
   * Whether a toggle of the rail outlives the page. Off, it starts at `defaultOpen` every time,
   * for a panel the page opens on its own terms rather than where the user last left it.
   */
  persistOpen?: boolean;
  /** Whether a drag of the rail outlives the page. */
  persistWidth?: boolean;
}

const SidebarProvider = ({
  defaultOpen,
  side = 'left',
  mode = 'auto',
  keyboardShortcut,
  storageKey = SIDEBAR_STORAGE_KEY,
  persistOpen = true,
  persistWidth = true,
  className,
  style,
  children,
  ref,
  ...props
}: ComponentProps<'div'> & SidebarProviderProps) => {
  const sidebarRef = useRef<HTMLElement>(null);
  const sidebarId = useId();

  // Rail expand/collapse only: as a drawer the popover owns openness.
  // A stored state is the user's own choice, so it wins over the default.
  const [open, setOpen] = useState(
    () => (persistOpen ? readStoredState(storageKey) : undefined) ?? defaultOpen ?? true
  );

  const isNarrow = useSyncExternalStore(subscribeToDrawer, getIsDrawer);
  const isDrawer = mode === 'drawer' || (mode === 'auto' && isNarrow);

  const [initialWidth] = useState(() => (persistWidth ? readStoredWidth(storageKey) : undefined));

  const persistedOpen = useRef(open);
  const activePointer = useRef<number | null>(null);

  useEffect(() => {
    if (persistedOpen.current === open) {
      return;
    }

    persistedOpen.current = open;
    if (persistOpen) {
      writeStoredState(storageKey, open);
    }
  }, [open, storageKey, persistOpen]);

  const resizeTo = useCallback(
    (next: number) => {
      const clamped = clampWidth(next);
      sidebarRef.current?.style.setProperty('--sidebar-width', `${clamped}px`);
      if (persistWidth) {
        writeStoredWidth(storageKey, clamped);
      }
    },
    [storageKey, persistWidth]
  );

  const resizeBy = useCallback(
    (delta: number) => {
      const sidebar = sidebarRef.current;
      if (!sidebar) {
        return;
      }

      const current = Number.parseFloat(getComputedStyle(sidebar).getPropertyValue('--sidebar-width'));
      if (current) {
        resizeTo(current + delta);
      }
    },
    [resizeTo]
  );

  const startResize = useCallback(
    (event: ReactPointerEvent) => {
      const sidebar = sidebarRef.current;
      if (!sidebar || isDrawer || event.button !== 0 || activePointer.current !== null) {
        return;
      }

      const { pointerId } = event;
      activePointer.current = pointerId;

      const { left, right } = sidebar.getBoundingClientRect();
      let isOpen = open;
      let next: number | undefined;
      let moved = false;

      const handleMove = (move: PointerEvent) => {
        if (move.pointerId !== pointerId) {
          return;
        }

        if (!moved) {
          moved = true;
          sidebar.dataset.resizing = '';
        }

        // The room under the panel's floor is where it shuts rather than where it stops, so one
        // drag both opens and closes it, and the rail's click is left to the pointer that never moved.
        const draggedWidth = side === 'right' ? right - move.clientX : move.clientX - left;
        const shouldOpen = draggedWidth > SIDEBAR_COLLAPSE_WIDTH;
        if (shouldOpen !== isOpen) {
          isOpen = shouldOpen;
          setOpen(shouldOpen);
        }

        if (!isOpen) {
          return;
        }

        next = clampWidth(draggedWidth);
        sidebar.style.setProperty('--sidebar-width', `${next}px`);
      };

      const handleEnd = (end: PointerEvent) => {
        if (end.pointerId !== pointerId) {
          return;
        }

        activePointer.current = null;
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleEnd);
        window.removeEventListener('pointercancel', handleEnd);
        delete sidebar.dataset.resizing;
        if (next !== undefined) {
          resizeTo(next);
        }
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleEnd);
      window.addEventListener('pointercancel', handleEnd);
    },
    [isDrawer, open, resizeTo, side]
  );

  const toggleSidebar = useCallback(() => {
    if (isDrawer) {
      sidebarRef.current?.togglePopover();
      return;
    }

    setOpen((open) => !open);
  }, [isDrawer]);

  useEffect(() => {
    if (!keyboardShortcut) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // A focused child may already own the chord, the rich text editor's bold being one.
      if (event.defaultPrevented) {
        return;
      }

      if (matchesShortcut(event, keyboardShortcut)) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar, keyboardShortcut]);

  const previousMode = useRef(isDrawer);
  useEffect(() => {
    if (previousMode.current === isDrawer) {
      return;
    }

    previousMode.current = isDrawer;
    const sidebar = sidebarRef.current;

    // A rail has no popover to be open, and the drawer would be left in the top layer.
    if (!isDrawer && sidebar?.matches(':popover-open')) {
      sidebar.hidePopover();
    }

    sidebar?.setAttribute('data-mode-change', '');
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => sidebar?.removeAttribute('data-mode-change'));
    });

    return () => cancelAnimationFrame(frame);
  }, [isDrawer]);

  // Collapsed is a rail-only state: the drawer is always its full width.
  const state = !isDrawer && !open ? 'collapsed' : 'expanded';

  const contextValue = useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      side,
      isDrawer,
      keyboardShortcut,
      toggleSidebar,
      sidebarRef,
      sidebarId,
      initialWidth,
      startResize,
      resizeBy,
    }),
    [state, open, side, isDrawer, keyboardShortcut, toggleSidebar, sidebarId, initialWidth, startResize, resizeBy]
  );

  return (
    <SidebarContext value={contextValue}>
      <div
        data-slot="sidebar-wrapper"
        ref={ref}
        className={cn('flex min-h-svh w-full', className)}
        style={style}
        {...props}
      >
        {children}
      </div>
    </SidebarContext>
  );
};

export { SidebarProvider, useSidebar };
